import { useEffect, useState } from "react";
import {
  type SummaryJobDetail,
  fetchSummaryJobDetail,
} from "../../api/admin-content";
import {
  LoadingIndicator,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";

/**
 * Wave 1B-C T-1BC.5 removed the "Retry Job" action: summary generation
 * now runs inside the Hashee Agent deployment (`apps/agent-hashee/`)
 * and the `POST /admin/summary-jobs/:id/retry` endpoint is gone. This
 * panel remains read-only for auditing historical jobs. `isFailed` and
 * `onRetryDone` props stay in the signature so the caller does not
 * need to change, but the retry button is no longer rendered.
 */
export function SummaryJobDetailPanel({
  jobId,
}: {
  jobId: string;
  isFailed?: boolean;
  onRetryDone?: () => void;
}): React.JSX.Element {
  const [detail, setDetail] = useState<SummaryJobDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSummaryJobDetail(jobId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading) return <LoadingIndicator />;
  if (!detail)
    return <p style={ts.emptyText}>{t("summary_job_detail.failed_to_load")}</p>;

  return (
    <div style={ts.detailPanel}>
      {detail.error_message && (
        <div style={ts.detailSection}>
          <h4 style={ts.detailTitle}>{t("summary_job_detail.error")}</h4>
          <p style={styles.errorText}>{detail.error_message}</p>
        </div>
      )}
      {detail.grant && (
        <div style={ts.detailSection}>
          <h4 style={ts.detailTitle}>{t("summary_job_detail.grant")}</h4>
          <DetailRow
            label={t("summary_job_detail.scope")}
            value={`${detail.grant.scope_type}:${detail.grant.scope_id.slice(0, 8)}`}
          />
          <DetailRow
            label={t("summary_job_detail.expires")}
            value={formatDate(detail.grant.expires_at)}
          />
        </div>
      )}
      {detail.summary_thread && (
        <div style={ts.detailSection}>
          <h4 style={ts.detailTitle}>{t("summary_job_detail.thread")}</h4>
          <DetailRow
            label={t("summary_job_detail.thread_status")}
            value={detail.summary_thread.status}
          />
          <DetailRow
            label={t("summary_job_detail.preview")}
            value={detail.summary_thread.title_preview ?? "-"}
          />
        </div>
      )}
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div style={ts.detailRow}>
      <span style={styles.detailLabel}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const styles: Record<string, React.CSSProperties> = {
  detailLabel: { color: "var(--color-text-secondary)" },
  errorText: {
    fontSize: 13,
    color: "var(--color-danger)",
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    margin: 0,
  },
};
